import DataKeys from 'k8s/DataKeys'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import {
  AccessRule,
  AccessRulesSummary,
  ApplicationCredential,
  ApplicationCredentialSelector,
} from './model'

const formatExpiration = (expiresAt: string | null): string => {
  if (!expiresAt) return 'Never'
  // OpenStack API returns datetime without Z suffix, treat as UTC
  const utcString = expiresAt.endsWith('Z') ? expiresAt : `${expiresAt}Z`
  const date = new Date(utcString)
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const computeAccessRulesSummary = (accessRules: AccessRule[] = []): AccessRulesSummary => {
  if (!accessRules || accessRules.length === 0) {
    return { servicesCount: 0, pathsCount: 0, methodsCount: 0 }
  }
  const uniqueServices = new Set(accessRules.map((rule) => rule.service))
  const uniquePaths = new Set(accessRules.map((rule) => rule.path))
  const uniqueMethods = new Set(accessRules.map((rule) => rule.method))
  return {
    servicesCount: uniqueServices.size,
    pathsCount: uniquePaths.size,
    methodsCount: uniqueMethods.size,
  }
}

export const applicationCredentialsSelector = createSharedSelector(
  getDataSelector<DataKeys.ApplicationCredentials>(DataKeys.ApplicationCredentials),
  (credentials: ApplicationCredential[]): ApplicationCredentialSelector[] => {
    return credentials.map((credential) => ({
      ...credential,
      formattedExpiration: formatExpiration(credential.expires_at),
      roleName: credential.roles?.[0]?.name || 'N/A',
      restricted: !credential.unrestricted,
      accessRulesCount: credential.access_rules?.length || 0,
      accessRulesSummary: computeAccessRulesSummary(credential.access_rules),
    }))
  },
)
